import { useEffect, useState } from 'react';

/**
 * Reports whether the Google Maps Places library has finished loading.
 *
 * The Maps script is injected once, asynchronously, by the loader in App.tsx, so
 * a component can render before it is available. That matters for Google's
 * <Autocomplete>: it asserts `google.maps.places` in componentDidMount and throws
 * an invariant when it is missing, which reaches the root error boundary and
 * unmounts the whole portal. Gate any such component on this hook and render a
 * plain input until it returns true.
 */
export const useGoogleMapsPlacesReady = (): boolean => {
  const [isReady, setIsReady] = useState<boolean>(() =>
    Boolean(window.google?.maps?.places),
  );

  useEffect(() => {
    if (isReady) return undefined;

    const interval = setInterval(() => {
      if (window.google?.maps?.places) {
        setIsReady(true);
      }
    }, 200);

    return () => clearInterval(interval);
  }, [isReady]);

  return isReady;
};
